/**
 * Image Resize Handler Component
 * Adds Word-like drag-to-resize functionality for images in content editor
 */

import { useEffect } from 'react';

interface ImageResizeHandlerProps {
  editorRef: React.RefObject<HTMLDivElement>;
  onContentChange: () => void;
  onBeforeDelete?: () => void;
}

export default function ImageResizeHandler({ editorRef, onContentChange, onBeforeDelete }: ImageResizeHandlerProps) {
  useEffect(() => {
    if (!editorRef.current) return;

    let selectedImage: HTMLImageElement | null = null;
    let resizeHandle: HTMLDivElement | null = null;
    let startX = 0;
    let startY = 0;
    let startWidth = 0;
    let startHeight = 0;

    // Add resize handles to image
    const addResizeHandles = (img: HTMLImageElement) => {
      // Remove existing handles
      removeResizeHandles();

      selectedImage = img;

      // Create resize handle wrapper
      const wrapper = document.createElement('div');
      wrapper.className = 'image-resize-wrapper';
      wrapper.style.cssText = `
        position: relative;
        display: inline-block;
        margin: 20px auto;
        max-width: 100%;
      `;

      // Wrap image
      const parent = img.parentNode;
      if (!parent) return;

      parent.insertBefore(wrapper, img);
      wrapper.appendChild(img);

      // Add corner resize handles
      const positions = ['nw', 'ne', 'sw', 'se'];
      positions.forEach(pos => {
        const handle = document.createElement('div');
        handle.className = `resize-handle resize-handle-${pos}`;
        handle.style.cssText = `
          position: absolute;
          width: 12px;
          height: 12px;
          background: #3b82f6;
          border: 2px solid white;
          border-radius: 50%;
          cursor: ${pos.includes('n') ? (pos.includes('w') ? 'nw' : 'ne') : (pos.includes('w') ? 'sw' : 'se')}-resize;
          z-index: 1000;
          ${pos.includes('n') ? 'top: -6px;' : 'bottom: -6px;'}
          ${pos.includes('w') ? 'left: -6px;' : 'right: -6px;'}
        `;
        handle.dataset.position = pos;
        wrapper.appendChild(handle);

        handle.addEventListener('mousedown', startResize);
      });

      // Add edge resize handles
      const edges = ['n', 's', 'e', 'w'];
      edges.forEach(edge => {
        const handle = document.createElement('div');
        handle.className = `resize-handle resize-handle-${edge}`;
        const isVertical = edge === 'n' || edge === 's';
        handle.style.cssText = `
          position: absolute;
          background: #3b82f6;
          cursor: ${isVertical ? 'ns' : 'ew'}-resize;
          z-index: 999;
          ${edge === 'n' ? 'top: -2px; left: 12px; right: 12px; height: 4px;' : ''}
          ${edge === 's' ? 'bottom: -2px; left: 12px; right: 12px; height: 4px;' : ''}
          ${edge === 'e' ? 'right: -2px; top: 12px; bottom: 12px; width: 4px;' : ''}
          ${edge === 'w' ? 'left: -2px; top: 12px; bottom: 12px; width: 4px;' : ''}
        `;
        handle.dataset.position = edge;
        wrapper.appendChild(handle);

        handle.addEventListener('mousedown', startResize);
      });

      // Add selection indicator
      img.style.outline = '2px solid #3b82f6';
      img.style.outlineOffset = '2px';

      // Create button container for delete
      const buttonContainer = document.createElement('div');
      buttonContainer.className = 'image-action-buttons';
      buttonContainer.style.cssText = `
        position: absolute;
        top: -14px;
        right: -14px;
        display: flex;
        gap: 4px;
        z-index: 1001;
      `;

      // Add delete button
      const deleteBtn = document.createElement('button');
      deleteBtn.className = 'image-delete-btn';
      deleteBtn.type = 'button';
      deleteBtn.title = 'Delete image';
      deleteBtn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      `;
      deleteBtn.style.cssText = `
        width: 28px;
        height: 28px;
        background: #ef4444;
        border: 2px solid white;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        transition: transform 0.15s, background-color 0.15s;
      `;

      deleteBtn.addEventListener('mouseenter', () => {
        deleteBtn.style.transform = 'scale(1.1)';
        deleteBtn.style.backgroundColor = '#dc2626';
      });

      deleteBtn.addEventListener('mouseleave', () => {
        deleteBtn.style.transform = 'scale(1)';
        deleteBtn.style.backgroundColor = '#ef4444';
      });

      deleteBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Save state before delete for undo support
        if (onBeforeDelete) {
          onBeforeDelete();
        }
        // Remove the wrapper and the image inside it
        const imgParent = wrapper.parentNode;
        if (imgParent) {
          // Check if image is wrapped in a paragraph
          if (imgParent.nodeName === 'P' && imgParent.childNodes.length <= 2) {
            imgParent.remove();
          } else {
            wrapper.remove();
          }
          onContentChange();
        }
        selectedImage = null;
      });

      deleteBtn.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
      });

      buttonContainer.appendChild(deleteBtn);
      wrapper.appendChild(buttonContainer);
    };

    // Remove resize handles
    const removeResizeHandles = () => {
      document.querySelectorAll('.image-resize-wrapper').forEach(wrapper => {
        const img = wrapper.querySelector('img');
        if (img) {
          img.style.outline = 'none';
          const parent = wrapper.parentNode;
          if (parent) {
            parent.insertBefore(img, wrapper);
            parent.removeChild(wrapper);
          }
        }
      });
      selectedImage = null;
    };

    // Start resize
    const startResize = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!selectedImage) return;

      const handle = e.target as HTMLDivElement;
      const position = handle.dataset.position;
      if (!position) return;

      resizeHandle = handle;
      startX = e.clientX;
      startY = e.clientY;
      startWidth = selectedImage.offsetWidth;
      startHeight = selectedImage.offsetHeight;

      document.addEventListener('mousemove', doResize);
      document.addEventListener('mouseup', stopResize);
    };

    // Perform resize
    const doResize = (e: MouseEvent) => {
      if (!selectedImage || !resizeHandle) return;

      const position = resizeHandle.dataset.position;
      if (!position) return;

      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      let newWidth = startWidth;
      let newHeight = startHeight;

      // Calculate new dimensions based on handle position
      if (position.includes('e')) {
        newWidth = startWidth + deltaX;
      } else if (position.includes('w')) {
        newWidth = startWidth - deltaX;
      }

      if (position.includes('s')) {
        newHeight = startHeight + deltaY;
      } else if (position.includes('n')) {
        newHeight = startHeight - deltaY;
      }

      // Apply minimum size
      newWidth = Math.max(100, newWidth);
      newHeight = Math.max(50, newHeight);

      // Apply dimensions
      if (position.includes('e') || position.includes('w')) {
        selectedImage.style.width = newWidth + 'px';
      }
      if (position.includes('n') || position.includes('s')) {
        selectedImage.style.height = newHeight + 'px';
      }

      // For corner handles, resize proportionally or freely
      if (position.length === 2) {
        // Corner resize - maintain aspect ratio if shift key held
        if (e.shiftKey) {
          const aspectRatio = startWidth / startHeight;
          if (Math.abs(deltaX) > Math.abs(deltaY)) {
            selectedImage.style.width = newWidth + 'px';
            selectedImage.style.height = (newWidth / aspectRatio) + 'px';
          } else {
            selectedImage.style.height = newHeight + 'px';
            selectedImage.style.width = (newHeight * aspectRatio) + 'px';
          }
        } else {
          // Free resize
          selectedImage.style.width = newWidth + 'px';
          selectedImage.style.height = newHeight + 'px';
        }
      }
    };

    // Stop resize
    const stopResize = () => {
      document.removeEventListener('mousemove', doResize);
      document.removeEventListener('mouseup', stopResize);
      resizeHandle = null;

      // Trigger content change
      onContentChange();
    };

    // Handle image clicks
    const handleImageClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'IMG') {
        e.stopPropagation();
        addResizeHandles(target as HTMLImageElement);
      }
    };

    // Handle clicks outside images
    const handleDocumentClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.tagName !== 'IMG' && !target.closest('.image-resize-wrapper')) {
        removeResizeHandles();
      }
    };

    // Attach event listeners
    const editor = editorRef.current;
    editor.addEventListener('click', handleImageClick);
    document.addEventListener('click', handleDocumentClick);

    // Cleanup
    return () => {
      editor.removeEventListener('click', handleImageClick);
      document.removeEventListener('click', handleDocumentClick);
      removeResizeHandles();
    };
  }, [editorRef, onContentChange, onBeforeDelete]);

  return null; // This is a behavior component, no UI
}
